<script setup lang="ts">
import dayjs from 'dayjs'
import { Color } from '@previo/codebooks'
import { $t } from '@previo/core'
import { ref } from 'vue'
import { useDateTimeRangePicker } from '../../../composables/DateTimeRangePickerState'
import PTextStandard from '../../../../typography/body/PTextStandard.vue'
import PContextMenu from '../../../../context-menu/PContextMenu.vue'
import { PContextMenuPositionEnum } from '../../../../context-menu/types'

type Emits = {
  (e: 'select'): void
}

const emits = defineEmits<Emits>()

const navigation = useDateTimeRangePicker()

const isMonthNavigationVisible = ref<boolean>(false)

const month = ref<number>(navigation.currentCalendar().month())
const offset = ref<number>(0)

const monthIndexes: number[] = Array.from({ length: 12 }, (_, i) => {
  return i
})

const selectMonth = (month: number): void => {
  emits('select')

  navigation.currentView.value = navigation.currentCalendar().month(month - offset.value)
  isMonthNavigationVisible.value = false
}

const open = (withMonth: number, withOffset: number = 0): void => {
  month.value = withMonth
  offset.value = withOffset
  isMonthNavigationVisible.value = true
}

const toggle = (withMonth: number, withOffset: number = 0): void => {
  month.value = withMonth
  offset.value = withOffset
  isMonthNavigationVisible.value = !isMonthNavigationVisible.value
}

defineExpose({
  open,
  toggle,
})
</script>

<template>
  <p-context-menu
    v-model="isMonthNavigationVisible"
    :title="$t('Select month')"
    :anchor="PContextMenuPositionEnum.BOTTOM_MIDDLE"
    :self="PContextMenuPositionEnum.TOP_MIDDLE"
    cy="date-time-range-picker-month-navigation"
  >
    <div class="ds-date-time-range-picker__month-navigation">
      <div
        v-for="monthIndex in monthIndexes"
        :key="monthIndex"
        :class="[
          'ds-date-time-range-picker__month-cell p-flex p-flex-center p-cursor-pointer p-non-selectable',
        ]"
        @click="selectMonth(monthIndex)"
      >
        <button
          :class="[
            'ds-date-time-range-picker__month-button p-flex p-flex-center p-cursor-pointer',
            {
              'ds-date-time-range-picker__month-button--selected': month === monthIndex,
            },
          ]"
        >
          <p-text-standard
            :text="dayjs().month(monthIndex).format('MMM').toUpperCase()"
            :color="month === monthIndex ? Color.WHITE : Color.GREY_500"
          />
        </button>
      </div>
    </div>
  </p-context-menu>
</template>

<style scoped lang="scss">
@import '../../../../../assets/scss/shared/variables/ds.radius';
@import '../../../../../assets/scss/shared/brand.module';

.ds-date-time-range-picker {
  &__month-navigation {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
  }

  &__month-button {
    height: 48px;
    border-radius: $radius-xl-mobile;
    border: none;
    outline: none;
    padding: 0 8px; // Added padding

    background-color: getColor('white') !important;

    &--selected {
      background-color: getColor('primary') !important;
    }

    &:hover:not(&--selected) {
      background-color: #f5f5f5;
    }
  }
}
</style>
